import { useState } from 'react';
import Icon from '@/components/icons/Icon';
import { classnames, isServer, roundRating } from '@/helpers';
import { customTwMerge } from '@/helpers/theme';
import { _t } from '@/locale';
import { Button } from '@/components/elements/forms/buttons';
import Popover from '@/components/elements/Popover';

const baseTranslationKey = 'components.elements.Rating';

/** Skeleton that mirrors the structure of the `lg` Rating (score, stars row, review count). */
export const RatingSkeleton = () => {
  const gradientBase = 'bg-surface_l3 animate-pulse';

  return (
    <div aria-hidden="true" className="gap-sm flex items-center">
      {/* Score, at the text-h-xl line height the real one occupies */}
      <div className={classnames(gradientBase, 'h-3xl w-4xl rounded')} />
      <div className="gap-2xs flex flex-col items-start">
        {/* Stars, matching the 16px icons the real one renders */}
        <div className="gap-2xs flex">
          {[1, 2, 3, 4, 5].map((star) => (
            <div key={star} className={classnames(gradientBase, 'h-md w-md rounded')} />
          ))}
        </div>
        <div className={classnames(gradientBase, 'h-md w-[56px] rounded')} />
      </div>
    </div>
  );
};

type Props = {
  avgRating: number;
  ratingCount?: number;
  reviewCount?: number;
  showCount?: boolean;
  hideCountLabel?: boolean;
  newMerchant?: boolean;
  size: 'sm' | 'md' | 'lg';
  className?: string;
  message?: string;
  ratingTextClassName?: string;
  showOnlyStars?: boolean;
  showOneStar?: boolean;
  withLowRatingCountInformation?: boolean;
  onReviewsClick?: () => void;
};

/** A dot used as a visual separator between two pieces of inline text. */
const renderDotSeparator = (colorClassName: string) => (
  <span className={classnames('h-xxs w-xxs rounded-full', colorClassName)} />
);

/**
 * A merchant with no ratings yet: swaps the score/stars for a "new merchant" message, with an
 * optional link to book instead of a review count.
 */
const NewMerchantRating = ({ size, onReviewsClick }: Pick<Props, 'size' | 'onReviewsClick'>) => {
  const showLink = Boolean(onReviewsClick);

  return (
    <div
      className={classnames('flex gap-2', {
        'flex-col': size === 'lg',
        'flex-row': size !== 'lg',
        'items-start': size === 'lg',
        'items-center': size !== 'lg',
      })}>
      <p
        className={classnames('text-fg_secondary ', {
          'text-m': size === 'sm',
          'text-l': size === 'md',
          'text-xl': size === 'lg',
        })}>
        {_t(`${baseTranslationKey}.newMerchant`)}
      </p>
      {size !== 'lg' ? (
        <div className="flex h-4 items-center justify-center">{renderDotSeparator('bg-black-300')}</div>
      ) : null}
      {showLink && (
        <Button
          onClick={onReviewsClick}
          variant={'link'}
          className={classnames('text-fg_link text-m whitespace-nowrap !p-0 underline', {
            'text-m': size === 'sm',
            'text-l': size !== 'sm',
          })}>
          {_t(`${baseTranslationKey}.bookTime`)}
        </Button>
      )}
    </div>
  );
};

/**
 * The info icon next to a low review count, explaining why on hover/focus. Uses the native
 * Popover API where supported; falls back to plain React state (kept visible instead of
 * toggled by the browser) where it isn't.
 */
const LowRatingInfoPopover = () => {
  const popoverId = 'oi1u2h1';
  const supportsPopover = !isServer && HTMLElement.prototype.hasOwnProperty('popover');
  const [isVisibleFallback, setIsVisibleFallback] = useState(false);

  const show = () => {
    setIsVisibleFallback(true);
    if (supportsPopover) {
      (document.getElementById(popoverId) as HTMLElement & { showPopover: () => void })?.showPopover();
    }
  };

  const hide = () => {
    setIsVisibleFallback(false);
    if (supportsPopover) {
      (document.getElementById(popoverId) as HTMLElement & { hidePopover: () => void })?.hidePopover();
    }
  };

  return (
    <>
      <button
        type="button"
        id={`${popoverId}-trigger`}
        {...{ popovertarget: popoverId }}
        className={classnames(
          `trigger-${popoverId}`,
          'focus-visible:outline-black-900 rounded-sm focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
        )}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        aria-describedby={popoverId}>
        <Icon variant="info-circle" color="fg_link" style={{ width: 20, height: 20 }} />
      </button>
      {(supportsPopover || isVisibleFallback) && (
        <Popover
          id={popoverId}
          className={classnames({
            'z-[100]': !supportsPopover,
            'mt-2': true,
          })}
          align={'right'}>
          <div className="space-y-md bg-bg_inverse p-md w-full max-w-[400px] rounded-lg">
            <p className="text-fg_secondary_inverse  text-m" id="popover-content">
              {_t('lowRatingCount')}
            </p>
          </div>
        </Popover>
      )}
    </>
  );
};

/**
 *
 * @deprecated This component should be replaced by the RatingV2
 *
 */
export const Rating = ({
  avgRating,
  ratingCount,
  reviewCount,
  showCount,
  hideCountLabel = false,
  size = 'sm',
  className = '',
  showOnlyStars,
  showOneStar,
  newMerchant,
  withLowRatingCountInformation,
  message,
  ratingTextClassName = 'text-fg_primary',
  onReviewsClick,
}: Props) => {
  const showLink = Boolean(onReviewsClick);
  const showsCountRow = size === 'sm' || (size === 'md' && showCount);

  const reviewLinkButtonClassName = classnames('text-fg_link text-m whitespace-nowrap !p-0 underline text-m');

  // Five stars scaled to the rating, or (when showOneStar) a single fully-lit star used as a
  // compact "★ 4.7" style indicator instead of a five-star row.
  const starsMarkup = showOneStar ? (
    <div className="w-md relative">
      <Icon variant="star-filled" color="black-300" style={{ width: 16, height: 16 }} />
      <Icon
        className="absolute inset-0"
        variant="star-filled"
        color="warning-500"
        style={{ width: 16, height: 16, clipPath: `inset(0 0% 0 0)` }}
      />
    </div>
  ) : (
    (() => {
      let restAreEmpty = false;
      return [1, 2, 3, 4, 5].map((starNumber, key) => {
        let fill = 100;
        if (starNumber > avgRating) {
          fill = !restAreEmpty ? (avgRating % 1) * 100 : 0;
          restAreEmpty = true;
        }
        return (
          <div key={key} className="w-md relative">
            <Icon variant="star-filled" color="black-300" style={{ width: 16, height: 16 }} />
            <Icon
              className="absolute inset-0"
              variant="star-filled"
              color="warning-500"
              style={{ width: 16, height: 16, clipPath: `inset(0 ${100 - fill}% 0 0)` }}
            />
          </div>
        );
      });
    })()
  );

  // "12 recensioner (34 betyg)" when both counts exist, otherwise just the rating count, or a
  // "no reviews" message. Used for the count row and the `lg` size's own count line.
  const countLabel =
    reviewCount > 0
      ? _t('reviewsFromRatingsCounts', { reviews: reviewCount, ratings: ratingCount })
      : ratingCount > 0
      ? `${ratingCount} ${_t(`${baseTranslationKey}.reviews`)}`
      : _t(`${baseTranslationKey}.notEnoughReviews`);

  // The count row's non-link variant: just the rating count (or its bare number, via
  // hideCountLabel), never the combined rating+review wording `countLabel` uses.
  const plainCountLabel =
    ratingCount > 0
      ? hideCountLabel
        ? ratingCount
        : `${ratingCount} ${_t(`${baseTranslationKey}.reviews`)}`
      : _t(`${baseTranslationKey}.notEnoughReviews`);

  if (newMerchant) {
    return <NewMerchantRating size={size} onReviewsClick={onReviewsClick} />;
  }

  const renderLargeRating = () => (
    <>
      {ratingCount > 0 && (
        <span
          className={customTwMerge(
            classnames(`text-h-xl flex items-center font-bold`, {
              [ratingTextClassName]: !!ratingTextClassName,
            }),
          )}>
          {roundRating(avgRating, 1).toFixed(1)}
        </span>
      )}
      <span
        className={customTwMerge(
          classnames('gap-2xs flex flex-col items-start', {
            'items-end': ratingCount === 0,
          }),
        )}>
        <span className="gap-2xs flex">{starsMarkup}</span>
        {!showLink && <span className="text-m text-fg_secondary whitespace-nowrap font-normal">{countLabel}</span>}
        {showLink && (
          <Button onClick={onReviewsClick} variant="link" className={reviewLinkButtonClassName}>
            {countLabel}
          </Button>
        )}
      </span>
    </>
  );

  const renderMediumRating = () => (
    <>
      {!showOnlyStars && ratingCount > 0 && (
        <span
          className={classnames('text-l flex items-center font-bold', {
            [ratingTextClassName]: !!ratingTextClassName,
          })}>
          {roundRating(avgRating, 1).toFixed(1)}
        </span>
      )}
      <span className="gap-2xs flex flex-col">
        <span className="gap-2xs flex">{starsMarkup}</span>
      </span>
    </>
  );

  const renderSmallRating = () => (
    <>
      {!showLink && <Icon variant="star-filled" color="warning-500" style={{ width: 16, height: 16 }} />}
      {ratingCount > 0 && (
        <span
          className={classnames('flex items-center font-bold', {
            [ratingTextClassName]: !!ratingTextClassName,
            'text-s': !showLink,
            'text-l': showLink,
          })}>
          {roundRating(avgRating, 1).toFixed(1)}
        </span>
      )}
      {showLink && <Icon variant="star-filled" color="warning-500" style={{ width: 16, height: 16 }} />}
    </>
  );

  return (
    <div
      className={customTwMerge(
        classnames('gap-xs inline-flex flex-row flex-wrap items-center justify-start', {
          'text-s': size === 'sm',
          'text-l': size !== 'sm',
          [className]: !!className,
        }),
      )}>
      {withLowRatingCountInformation && <LowRatingInfoPopover />}
      <div className={`${size === 'lg' ? 'gap-sm' : 'gap-2xs'} flex select-none items-center`}>
        {size === 'lg' && renderLargeRating()}
        {size === 'md' && renderMediumRating()}
        {size === 'sm' && renderSmallRating()}
      </div>
      {showsCountRow && (
        <span className={classnames(size === 'sm' ? 'text-s' : 'text-m', 'text-fg_secondary flex items-center')}>
          {size === 'md' && renderDotSeparator('bg-fg_secondary mr-xs')}
          {showLink && (
            <Button onClick={onReviewsClick} variant="link" className={reviewLinkButtonClassName}>
              {countLabel}
            </Button>
          )}
          {!showLink && <span>{plainCountLabel}</span>}
        </span>
      )}
      {message && <p className="text-m text-fg_secondary">{message}</p>}
    </div>
  );
};

export default Rating;
